import React, { Suspense } from 'react';
import { Navigate, Route, createRoutesFromElements } from 'react-router-dom';
import { useUser } from './shared/use-user';
import { pagePathnames, pageRoutes } from './pages/page-routes';
import { useRouting } from './shared/use-routing';
import { App } from './App';
import { Loader } from './components/Loader';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { LangGuard } from './shared/LangGuard';

function dummyLayout(props) {
    return props.children;
}

function ProtectedRoute({ page: Page, layout, container, containerProps, ...props }) {
    const { isLoading, userData } = useUser();
    const { location, buildPath } = useRouting();

    if (isLoading) {
        return <div style={{ height: '100vh' }} />;
    }

    if (!userData.isAuthenticated) {
        return <Navigate to={buildPath(pagePathnames.start)} replace />;
    }

    const Layout = layout ? layout : dummyLayout;
    const Container = container ? container : dummyLayout;

    return (
        <Layout>
            <Container {...containerProps}>
                <Suspense
                    key={location.key}
                    fallback={
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '24px 0' }}>
                            <Loader variant='secondary' />
                        </div>
                    }
                >
                    <Page {...props} />
                </Suspense>
            </Container>
        </Layout>
    );
}

function PublicRoute({ page: Page, layout, container, containerProps, ...props }) { /* eslint-disable-line */
    const { isLoading, userData } = useUser();
    const { location, buildPath, stripLangPrefix } = useRouting();

    if (stripLangPrefix(location.pathname) === pagePathnames.signIn) {
        if (isLoading) {
            return <div style={{ height: '100vh' }} />;
        }

        if (userData.isAuthenticated) {
            return <Navigate to={buildPath(pagePathnames.dashboard)} replace />;
        }
    }

    const Layout = layout ? layout : dummyLayout;

    return (
        <Layout>
            <Suspense
                key={location.key}
                fallback={
                    <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Loader variant='secondary' />
                    </div>
                }
            >
                <Page {...props} />
            </Suspense>
        </Layout>
    );
}

function renderPageRoute({ isProtected, page, layout, container, containerProps, path }) {
    const RouteElement = isProtected ? ProtectedRoute : PublicRoute;
    const element = (
        <RouteElement
            page={page}
            layout={layout}
            container={container}
            containerProps={containerProps}
        />
    );

    if (path === pagePathnames.start) {
        return <Route index key={path} element={element} />;
    }

    return (
        <Route
            path={path.replace(/^\//, '')}
            key={path}
            element={element}
        />
    );
}

function getAppRoutes() {
    const startPageRoute = pageRoutes.find(({ path }) => path === pagePathnames.start);

    return createRoutesFromElements(
        <>
            <Route element={<App />} errorElement={<AppErrorBoundary />}>
                {renderPageRoute(startPageRoute)}
            </Route>
            <Route path=':lang' element={<LangGuard />}>
                <Route element={<App />} errorElement={<AppErrorBoundary />}>
                    {pageRoutes.map(renderPageRoute)}
                </Route>
            </Route>
        </>
    );
}

export {
    getAppRoutes
};
